// Auth domain entities — pure types/value objects.
// No React, no fetch, no Office.js. The seam between the rest of the
// add-in and any auth implementation lives in `./ports.ts`.

/** Credentials submitted by the user from the LoginScreen. */
export interface Credentials {
  email: string
  password: string
}

/** Server-issued session payload. Mirrors `TokenResponse` from the BE
 *  (`theplaidoyer-api/app/schemas/identity/auth.py::TokenResponse`).
 *  The BE also sets HttpOnly cookies in the same response; we keep the
 *  bearer token on disk so a future PR-3 SSE consumer / fetch helper
 *  can attach `Authorization: Bearer ...` if Office's iframe sandbox
 *  blocks third-party cookies (a known risk on Word desktop on macOS). */
export interface AuthSession {
  accessToken: string
  userId: string
  email: string
  isAdmin: boolean
  isOwner: boolean
}

/** Discriminated error union surfaced to the UI by the sign-in usecase.
 *  Distinguishing "wrong credentials" (re-prompt the user) from "network
 *  failure" (offer retry) is essential UX in the 380px taskpane where
 *  there's no global error surface like the SaaS Topbar toast.
 *
 *  Values are kept in lockstep with ``HttpErrorKind`` in
 *  ``src/adapters/http/_errors.ts`` (PR-3c / THE-487 alignment) — same
 *  six base values + one auth-specific extension. We duplicate the
 *  literals here instead of importing because ``src/domain/`` may not
 *  depend on ``src/adapters/`` (hexagonal boundary enforced by ESLint).
 *  The pinning test at ``__tests__/auth-error-kind.test.ts`` fails at
 *  tsc time if either side drifts. */
export type AuthErrorKind =
  | 'auth'
  | 'rate_limited'
  | 'network'
  | 'server'
  | 'malformed'
  | 'unknown'
  | 'invalid_credentials'

export class AuthError extends Error {
  constructor(
    public readonly kind: AuthErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'AuthError'
  }
}
